import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiConsumer } from '../../../service/api-consumer';

import { DeviceListResponse } from '../../../interface/response/device-list/DeviceListResponse';

import { Warning } from '../../../enum/health-monitor/Warning';
import { SearchButton } from '../../../enum/health-monitor/SearchButton';
import { ColumnName } from '../../../enum/health-monitor/ColumnName';
import { HealthMonitorGridAccessor } from '../../../enum/health-monitor/HealthMonitorGridAccessor';
import { AutocompleteListParams } from '../../../interface/request/AutocompleteListParams';
import { RootState } from '../../store';

interface DeviceList {
  deviceList: DeviceListResponse | null;
  isLoadingDeviceList: boolean;
  totalCount: number;
  totalOffline: number;
  totalOnline: number;
  totalWarnings: number;
  shellWarnings: number;
  moneyWarnings: number;
  printerWarnings: number;
  totalWarningsByType: number;
  activeSearchButton: SearchButton;
  activeCheckbox: string;
  countForPagination: number;
  currentPage: number;
  resultingPage: number;
  selectedRowsPerPage: number;
  lastPage: number;
  searchInput: string | null;
  sortColumn: ColumnName;
  columnToSearchBy: ColumnName;
  isDesc: boolean;
  autocompleteList: string[];
  filteredCount: number;
}

const initialState: DeviceList = {
  deviceList: null,
  isLoadingDeviceList: false,
  totalCount: 0,
  totalOffline: 0,
  totalOnline: 0,
  totalWarnings: 0,
  shellWarnings: 0,
  moneyWarnings: 0,
  printerWarnings: 0,
  totalWarningsByType: 0,
  activeSearchButton: SearchButton.ALL,
  activeCheckbox: '',
  countForPagination: 1,
  currentPage: 0,
  resultingPage: 0,
  selectedRowsPerPage: 20,
  lastPage: 1,
  searchInput: null,
  sortColumn: ColumnName.NAME,
  columnToSearchBy: ColumnName.NAME,
  isDesc: true,
  autocompleteList: [],
  filteredCount: 0,
};

export const getDeviceList = createAsyncThunk('/deviceList', async (_, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const deviceList = state.deviceList;

  const response = await ApiConsumer.getDeviceList(
    deviceList.currentPage,
    deviceList.selectedRowsPerPage,
    deviceList.isDesc,
    deviceList.sortColumn,
    deviceList.columnToSearchBy,
    deviceList.searchInput || '',
    deviceList.activeCheckbox?.slice(0, -1),
    deviceList.activeSearchButton,
  );

  return {
    ...response,
    page: deviceList.currentPage,
  };
});

export const getAutocompleteList = createAsyncThunk('/autocomplete', async (data: AutocompleteListParams) => {
  const response = await ApiConsumer.getAutocompleteList(data.search, data.column, data.maxSize);

  return response;
});

export const deviceListSlice = createSlice({
  name: 'deviceList',
  initialState,
  reducers: {
    setActiveCheckbox: (state, { payload }) => {
      if (payload !== '') {
        if (!state.activeCheckbox.includes(`${payload},`)) {
          state.activeCheckbox += `${payload},`;
        } else {
          state.activeCheckbox = state.activeCheckbox.replace(`${payload},`, '');
        }
      } else {
        state.activeCheckbox = '';
      }
    },
    setCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
    setLastPage: (state, { payload }) => {
      state.lastPage = payload;
    },
    setSearchInput: (state, { payload }) => {
      state.searchInput = payload;
    },
    clearAutocompleteList: (state) => {
      state.autocompleteList = [];
    },
    setActiveSearchButton: (state, { payload }) => {
      state.activeSearchButton = payload;
    },
    setSortColumn: (state, { payload }) => {
      switch (payload) {
        case HealthMonitorGridAccessor.LOCATION_NAME:
          state.sortColumn = ColumnName.BETSHOP;
          break;
        case HealthMonitorGridAccessor.STATUS:
          state.sortColumn = ColumnName.SHELL_STATUS;
          break;
        default:
          state.sortColumn = ColumnName.NAME;
          break;
      }
    },
    setColumnToSearchBy: (state, { payload }) => {
      state.columnToSearchBy = payload;
    },
    setIsDesc: (state, { payload }) => {
      state.isDesc = payload;
    },
    setColumnOrder: (state, { payload }) => {
      switch (payload) {
        case HealthMonitorGridAccessor.LOCATION_NAME:
          if (state.sortColumn === ColumnName.BETSHOP) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
        case HealthMonitorGridAccessor.STATUS:
          if (state.sortColumn === ColumnName.SHELL_STATUS) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
        default:
          if (state.sortColumn === ColumnName.NAME) {
            state.isDesc = !state.isDesc;
          } else {
            state.isDesc = true;
          }
          break;
      }
    },
    setSelectedRowsPerPage: (state, { payload }) => {
      state.selectedRowsPerPage = payload;

      const tempLastPage = Math.ceil(state.countForPagination / state.selectedRowsPerPage) - 1;

      if (state.currentPage > tempLastPage) {
        state.currentPage = tempLastPage;
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDeviceList.fulfilled, (state, { payload }) => {
      const { totalCount, totalOffline, totalOnline, totalWarnings, filteredCount, totalWarningsByType, page } =
        payload;

      state.deviceList = payload;

      state.totalCount = totalCount;
      state.totalOffline = totalOffline;
      state.totalOnline = totalOnline;
      state.totalWarnings = totalWarnings;
      state.filteredCount = filteredCount;

      const warningsByType = JSON.parse(totalWarningsByType || '{}');

      state.shellWarnings = warningsByType[Warning.SHELL] ?? 0;
      state.moneyWarnings = warningsByType[Warning.MONEY] ?? 0;
      state.printerWarnings = warningsByType[Warning.PRINTER] ?? 0;

      state.totalWarningsByType = state.shellWarnings + state.moneyWarnings + state.printerWarnings;

      const lastPage = Math.max(1, Math.ceil(filteredCount / state.selectedRowsPerPage));

      state.lastPage = lastPage;

      const maxPageIndex = lastPage - 1;

      const requestedPage = page ?? state.currentPage;

      state.currentPage = Math.min(Math.max(requestedPage, 0), maxPageIndex);

      state.resultingPage = state.currentPage;

      state.isLoadingDeviceList = false;
    });

    builder.addCase(getDeviceList.pending, (state) => {
      state.isLoadingDeviceList = true;
    });

    builder.addCase(getDeviceList.rejected, (state) => {
      state.isLoadingDeviceList = false;
    });

    builder.addCase(getAutocompleteList.fulfilled, (state, { payload }) => {
      state.autocompleteList = payload.results;
      // state.isLoadingDeviceList = false;
    });

    builder.addCase(getAutocompleteList.pending, (state) => {
      // state.isLoadingDeviceList = true;
    });

    builder.addCase(getAutocompleteList.rejected, (state) => {
      // state.isLoadingDeviceList = false;
    });
  },
});

export const {
  setActiveCheckbox,
  setCurrentPage,
  setLastPage,
  setSearchInput,
  clearAutocompleteList,
  setActiveSearchButton,
  setSortColumn,
  setIsDesc,
  setColumnToSearchBy,
  setColumnOrder,
  setSelectedRowsPerPage,
} = deviceListSlice.actions;

export default deviceListSlice.reducer;
